import recaptcha from '@components/App/RecaptchaWidget/recaptcha'
import { useEffect, useRef, useState } from 'react'

import type {
  CrawlerErrorData,
  CrawlerExtra,
  CrawlerFailureReason,
  CrawlerIssueItem,
  CrawlerProgress,
  CrawlerRedirect,
  ImportSitemapResultUrlItem,
} from '@/types'

type UseCrawlerProps = {
  url: string | null
  onFinished?: () => void
  onStopped?: () => void
}

type UseCrawlerResult = {
  id: string | null
  redirect: CrawlerRedirect | null
  siteUrl: string | null
  progress: CrawlerProgress
  result: ImportSitemapResultUrlItem[]
  // Every URL that did not become a page (OCT-4848). Sent once, with the done
  // event. The visual import ignores it; the sitemap report needs it.
  issues: CrawlerIssueItem[]
  extra?: CrawlerExtra
  error: string
  done: boolean
  close: () => void
}

const initialProgress = {
  progressPercentage: 0,
  scanned: 0,
  totalCount: 0,
  url: '',
  details: {
    countSkippedByTimeout: 0,
    countFailed: 0,
    countSizeExceeded: 0,
    countAbsent: 0,
    countDenied: 0,
    countDomainNotFound: 0,
  },
  limitExceeded: false,
  timeoutExceeded: false,
}

const CRAWLER_ERROR_MESSAGES: Partial<Record<CrawlerFailureReason, string>> = {
  domain_not_found: "We couldn't find that domain. Check the address.",
  crawler_blocked: "This site blocks crawlers, so it can't be imported.",
}

const getCrawlerErrorMessage = ({
  message,
  reason,
}: CrawlerErrorData): string =>
  (reason && CRAWLER_ERROR_MESSAGES[reason]) || message

const useCrawler = ({
  url,
  onFinished,
  onStopped,
}: UseCrawlerProps): UseCrawlerResult => {
  const eventSource = useRef<EventSource | null>(null)
  const [id, setId] = useState<string | null>(null)
  const [progress, setProgress] = useState<CrawlerProgress>(initialProgress)
  const [result, setResult] = useState<ImportSitemapResultUrlItem[]>([])
  const [issues, setIssues] = useState<CrawlerIssueItem[]>([])
  const [done, setDone] = useState<boolean>(false)
  const [extra, setExtra] = useState<CrawlerExtra>()
  const [error, setError] = useState('')
  const [redirect, setRedirect] = useState<CrawlerRedirect | null>(null)
  const [siteUrl, setSiteUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    if (url) {
      run(() => active).catch(() => {
        if (active) {
          setError('Could not start the crawl. Please try again.')
        }
      })
    }
    return () => {
      active = false
      eventSource.current?.close()
    }
  }, [url])

  const run = async (isActive: () => boolean) => {
    eventSource.current?.close()
    const token = await recaptcha.execute({ action: 'crawler_run' })
    if (!isActive()) {
      return
    }
    const parsedUrl = new URL(url!)
    const website = parsedUrl.searchParams.get('url')

    if (token) {
      parsedUrl.searchParams.set('response_key', token)
    }

    const events = new EventSource(parsedUrl.toString())
    eventSource.current = events

    events.onerror = () => {
      setError(
        `There was an error while accessing the URL specified: ${website}`
      )

      events.close()
    }

    events.onmessage = async (event) => {
      const parsedResult = JSON.parse(event.data)

      switch (parsedResult.type) {
        case 'redirect':
          events.close()
          setRedirect(parsedResult.data)
          break
        case 'stop':
          events.close()
          if (onStopped) {
            onStopped()
          }
          break
        case 'done':
          // Terminal whether or not any page came back. The crawler ends the response
          // after a terminal event, and an EventSource left open treats that as a
          // dropped connection and redials — starting a fresh crawl of the same site.
          events.close()

          if (
            parsedResult.data &&
            parsedResult.data.data &&
            parsedResult.data.data.length > 0
          ) {
            setSiteUrl(parsedResult.data.siteUrl || website)
            setResult(parsedResult.data.data)
            setIssues(parsedResult.data.issues || [])
            setExtra(parsedResult.data.extra)
            setId(parsedResult.id)
            setDone(true)
            if (onFinished) {
              onFinished()
            }
          } else {
            setError(
              `There was an error while accessing the URL specified: ${website}`
            )
          }

          break
        case 'error':
          if (parsedResult.data) {
            setError(getCrawlerErrorMessage(parsedResult.data))
          }
          setId(parsedResult.id)
          events.close()

          break
        default: {
          const { progress } = parsedResult
          progress.siteUrl && setSiteUrl(progress.siteUrl)
          setProgress(progress)
          progress.resultPage &&
            setResult((result) => [...result, progress.resultPage])
          progress.extra && setExtra(progress.extra)
          setId(parsedResult.id)

          break
        }
      }
    }
  }

  return {
    id,
    redirect,
    siteUrl,
    progress,
    result,
    issues,
    extra,
    error,
    done,
    close: () => eventSource.current?.close(),
  }
}

export default useCrawler
